/**
 * @file ArchiveOrderFlow.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-05
 * @modified 2026-10-05
 * @description Archiving one customer order — the whole sequence, in one place,
 *              so every screen that offers it runs the same one: Search
 *              Customer Orders, and the two transport planning boards.
 *
 *              1. GET  /api/orders/customer-orders/:id/archive-preview — the
 *                 order's live PO-Ds and PO-H, each with whether it can go
 *                 (poArchiveService.forOrder), and whether that day's driver
 *                 paperwork has already gone out.
 *              2. No live POs: archived straight away, as it always was.
 *                 Otherwise ArchivePOsFlow — the checklist, then a cancellation
 *                 email for each sent PO the user left ticked, then the archive.
 *              3. PATCH /api/orders/customer-orders/:id/archive — the order and
 *                 the ticked POs together, in one transaction, audited.
 *
 *              Moved out of SearchCustomerOrders.js unchanged (2026-10-05) so
 *              the boards could reuse it rather than copy it. `source` is the
 *              board's label (utils/archiveSource.js on the server), which puts
 *              the archive in that day's Plan History as a plan change.
 */

import React, { useEffect, useRef, useState } from 'react';
import ArchivePOsFlow from './ArchivePOsFlow';
import { API_URL } from '../config';
import { formatDate } from '../utils/dateFormatters';

/** The order's live POs and paperwork state, for the archive checklist. */
export async function fetchOrderArchivePreview(orderId) {
  const res = await fetch(`${API_URL}/api/orders/customer-orders/${orderId}/archive-preview`, { credentials: 'include' });
  const preview = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(preview.error || 'Could not check the order\'s purchase orders.');
  return preview;
}

/** Archive (or restore) an order, with the POs ticked to go with it. */
export async function patchOrderArchive(orderId, archived, archivePoIds = [], { source = null } = {}) {
  const res = await fetch(`${API_URL}/api/orders/customer-orders/${orderId}/archive`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ archived, archivePoIds, ...(source ? { source } : {}) })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Could not ${archived ? 'archive' : 'restore'} the order.`);
  return data;
}

/**
 * What to tell the user afterwards. Archived orders leave the planning boards;
 * a PO the user chose to keep that a supplier or haulier already has is still
 * theirs — say who to tell.
 *
 * @returns {{ type: 'success'|'warning', message: string }}
 */
export function orderArchiveOutcome(order, archived, data, extraNote = '') {
  const sent = Array.isArray(data?.sent_pos) ? data.sent_pos : [];
  const withPos = Array.isArray(data?.archived_pos) && data.archived_pos.length
    ? ` with ${data.archived_pos.map(p => p.purchase_order_number).join(', ')}` : '';
  return {
    type: sent.length || extraNote ? 'warning' : 'success',
    message: `${order.order_number} ${archived ? `archived${withPos}` : 'restored'}${archived ? ' — it is off the planning boards' : ''}.`
      + (sent.length
        ? ` ${sent.map(p => `${p.purchase_order_number} (${p.contact_name || (p.po_type === 'haulage' ? 'haulier' : 'supplier')})`).join(', ')}`
          + ` was kept and had already been sent — let ${sent.length === 1 ? 'them' : 'each of them'} know.`
        : '')
      + (extraNote ? ` ${extraNote}` : '')
  };
}

/**
 * @param {object}   order    { id, order_number }
 * @param {object}   user     For "send as" in the cancellation emails.
 * @param {string}   source   Optional board label (see the file header).
 * @param {function} onDone   ({ type, message, data? }) — archived, or an error
 *                            before the checklist opened. The caller closes the
 *                            flow, shows the message and re-reads its rows.
 * @param {function} onClose  Cancelled from the checklist; nothing changed.
 */
function ArchiveOrderFlow({ order, user, source = null, onDone, onClose }) {
  // { order, pos, paperworkNote }. The order is the one the preview was read
  // for, kept with its POs: if the `order` prop changed while the preview was
  // loading (another row chosen), the checklist must still archive the order
  // its PO ids belong to, never the new one with the old one's POs.
  const [checklist, setChecklist] = useState(null);
  // Once per mount: an effect that ran twice would archive twice.
  const started = useRef(false);
  // Cleared on unmount, so a flow closed or replaced while its preview was
  // loading never goes on to archive. (Its own effect, so StrictMode's
  // mount → unmount → mount leaves it set.)
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const target = order;
    (async () => {
      try {
        const preview = await fetchOrderArchivePreview(target.id);
        if (!alive.current) return;
        const paperworkNote = preview.paperwork_sent_at
          ? `Driver paperwork for ${formatDate(preview.order?.delivery_date)} was already sent — tell the driver this delivery is off.`
          : '';
        if (!preview.pos?.length) {
          const data = await patchOrderArchive(target.id, true, [], { source });
          onDone({ ...orderArchiveOutcome(target, true, data, paperworkNote), data });
          return;
        }
        setChecklist({ order: target, pos: preview.pos, paperworkNote });
      } catch (error) {
        if (alive.current) onDone({ type: 'error', message: error.message });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!checklist) return null;
  const target = checklist.order;
  return (
    <ArchivePOsFlow
      title={`Archive ${target.order_number}`}
      intro={<>Archiving <strong>{target.order_number}</strong> takes it off the planning boards. Tick the purchase orders to archive with it — any a supplier or haulier already has can be sent a cancellation first. Unticked ones are kept as they are.</>}
      note={checklist.paperworkNote}
      pos={checklist.pos}
      allowNone
      reason={`customer order ${target.order_number} is cancelled`}
      confirmLabel={(n) => (n ? `Archive order + ${n} PO${n === 1 ? '' : 's'}` : 'Archive order only')}
      user={user}
      onArchive={async (ids) => {
        // A refusal throws, and ArchivePOsFlow shows it and stays open.
        const data = await patchOrderArchive(target.id, true, ids, { source });
        onDone({ ...orderArchiveOutcome(target, true, data, checklist.paperworkNote), data });
      }}
      onClose={onClose}
    />
  );
}

export default ArchiveOrderFlow;
